import { useState, useEffect } from 'react';
import { Box, Heading, Text, Container, VStack, Button } from '@chakra-ui/react';
import { heroBackground } from '../lib/storage';

interface HeroProps {
  onNavigateToContact: () => void;
}

const Hero: React.FC<HeroProps> = ({ onNavigateToContact }) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box id="home" position="relative" h={{ base: '100vh', md: '100vh' }} overflow="hidden">
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgImage={`url(${heroBackground})`}
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        transform={`translateY(${scrollY * 0.5}px)`}
        transition="transform 0.1s ease-out"
        _after={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: 'linear-gradient(to bottom, #bf9d6de6, #d4bfa0a6)',
        }}
      />

      <Container
        maxW="1400px"
        h="full"
        position="relative"
        zIndex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={8} textAlign="center" color="yaleBlue" maxW="900px" px={{ base: 4, md: 8 }}>
          <Heading
            as="h1"
            fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
            fontWeight={500}
            lineHeight={1.2}
            animation="fadeInUp 1s ease-out"
          >
            Assessoria Jurídica Estratégica e Preventiva
          </Heading>

          <Text
            fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
            fontWeight={400}
            maxW="700px"
            color="graphite"
            animation="fadeInUp 1.2s ease-out"
          >
            Oferecemos soluções jurídicas acessíveis e seguras em todo o
            território nacional, com atendimento especializado e humanizado.
          </Text>

          <VStack spacing={4} pt={8} animation="fadeInUp 1.4s ease-out">
            <Button
              variant="primary"
              size="lg"
              px={12}
              py={8}
              fontSize={{ base: 'md', md: 'lg' }}
              fontWeight={400}
              onClick={onNavigateToContact}
              _hover={{
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px rgba(34, 30, 31, 0.35)',
              }}
            >
              Agende uma Consulta
            </Button>

            <Text fontSize="sm" color="graphite" fontWeight={600}>
              Atendimento presencial e online
            </Text>
          </VStack>
        </VStack>
      </Container>

      <Box position="absolute" bottom={8} left="50%" transform="translateX(-50%)" animation="bounce 2s infinite">
        <Box w="32px" h="48px" border="2px solid" borderColor="graphite" borderRadius="16px" position="relative">
          <Box
            w="4px"
            h="8px"
            bg="graphite"
            borderRadius="2px"
            position="absolute"
            top="8px"
            left="50%"
            transform="translateX(-50%)"
            animation="scrollIndicator 1.5s infinite"
          />
        </Box>
      </Box>

      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(40px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateX(-50%) translateY(0);
            }
            40% {
              transform: translateX(-50%) translateY(-10px);
            }
            60% {
              transform: translateX(-50%) translateY(-5px);
            }
          }

          @keyframes scrollIndicator {
            0% {
              opacity: 0;
              top: 8px;
            }
            50% {
              opacity: 1;
            }
            100% {
              opacity: 0;
              top: 24px;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default Hero;
